import { captureEvent } from "@Light/app/analytics";
import { OptOutFilter, OptOutReasonOption } from "@Light/services/lightTypes";
import { useLight } from "@Light/services/light";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { OptOutSurvey, type OptOutSelection } from "./OptOutSurvey";

function areOptOutReasonsReady(
  reasons: OptOutReasonOption[] | undefined,
): reasons is OptOutReasonOption[] {
  return reasons !== undefined && reasons.length > 0;
}

type RecordEnrollmentOptOut = ReturnType<
  ReturnType<typeof useLight>["useRecordEnrollmentOptOutMutation"]
>[0];

async function recordOptOutAndClose({
  selection,
  exitSource,
  filter,
  planUuids,
  recordOptOut,
  onConfirmClose,
  onComplete,
}: {
  selection: OptOutSelection | undefined;
  exitSource: string | undefined;
  filter: OptOutFilter;
  planUuids: string[];
  recordOptOut: RecordEnrollmentOptOut;
  onConfirmClose: (source?: string) => void;
  onComplete: () => void;
}) {
  try {
    await recordOptOut({
      reason: selection?.reason,
      label: selection?.label,
      plan_uuids: planUuids.length > 0 ? planUuids : undefined,
      flow: filter,
      detail: selection?.detail,
    }).unwrap();
  } catch {
    captureEvent("enrollment_opt_out_failed", {
      filter,
      source: exitSource ?? "other",
    });
  } finally {
    onComplete();
    onConfirmClose(exitSource);
  }
}

type OptOutSurveyContextValue = {
  requestClose: (source?: string) => void;
  registerPlanUuid: (planUuid: string) => void;
  markRatePageSeen: () => void;
  markCompleted: () => void;
};

const OptOutSurveyContext = createContext<OptOutSurveyContextValue | null>(
  null,
);

export type OptOutSurveyProviderProps = {
  filter: OptOutFilter;
  onConfirmClose: (source?: string) => void;
  children: React.ReactNode;
};

export function OptOutSurveyProvider({
  filter,
  onConfirmClose,
  children,
}: OptOutSurveyProviderProps) {
  const { useGetOptOutReasonsQuery, useRecordEnrollmentOptOutMutation } =
    useLight();
  const reasonsQuery = useGetOptOutReasonsQuery(filter);
  const [recordOptOut] = useRecordEnrollmentOptOutMutation();
  const [open, setOpen] = useState(false);
  const [pendingSource, setPendingSource] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const planUuidsRef = useRef<string[]>([]);
  const hasSeenRatePageRef = useRef(false);
  const hasCompletedRef = useRef(false);

  const markRatePageSeen = () => {
    hasSeenRatePageRef.current = true;
  };

  const markCompleted = () => {
    hasCompletedRef.current = true;
  };

  const registerPlanUuid = (planUuid: string) => {
    if (!planUuid) return;
    if (!planUuidsRef.current.includes(planUuid)) {
      planUuidsRef.current = [...planUuidsRef.current, planUuid];
    }
  };

  const requestClose = (source?: string) => {
    if (!hasSeenRatePageRef.current || hasCompletedRef.current) {
      onConfirmClose(source);
      return;
    }

    if (!areOptOutReasonsReady(reasonsQuery.data)) {
      void handleExit(undefined, source);
      return;
    }

    setPendingSource(source);
    setOpen(true);
    captureEvent("opt_out_survey_opened", {
      filter,
      source: source ?? "other",
    });
  };

  const handleDismiss = () => {
    setOpen(false);
    setPendingSource(undefined);
  };

  const handleExit = async (
    selection: OptOutSelection | undefined,
    source?: string,
  ) => {
    const exitSource = source ?? pendingSource;
    setIsSubmitting(true);
    await recordOptOutAndClose({
      selection,
      exitSource,
      filter,
      planUuids: planUuidsRef.current,
      recordOptOut,
      onConfirmClose,
      onComplete: () => {
        setIsSubmitting(false);
        setOpen(false);
        setPendingSource(undefined);
      },
    });
  };

  return (
    <OptOutSurveyContext.Provider
      value={{
        requestClose,
        registerPlanUuid,
        markRatePageSeen,
        markCompleted,
      }}
    >
      {children}
      {areOptOutReasonsReady(reasonsQuery.data) ? (
        <OptOutSurvey
          open={open}
          reasons={reasonsQuery.data}
          isSubmitting={isSubmitting}
          onClose={handleDismiss}
          onExit={handleExit}
        />
      ) : null}
    </OptOutSurveyContext.Provider>
  );
}

export function useOptOutSurvey(): OptOutSurveyContextValue {
  const context = useContext(OptOutSurveyContext);
  if (!context) {
    throw new Error("useOptOutSurvey must be used within OptOutSurveyProvider");
  }
  return context;
}

export function useOptionalOptOutSurvey(): OptOutSurveyContextValue | null {
  return useContext(OptOutSurveyContext);
}

export function useOptOutPlanTracking(planUuid?: string | null): void {
  const context = useOptionalOptOutSurvey();
  const registerPlanUuid = context?.registerPlanUuid;

  useEffect(() => {
    if (planUuid && registerPlanUuid) {
      registerPlanUuid(planUuid);
    }
  }, [planUuid, registerPlanUuid]);
}

/** Call from rate screens so the opt-out survey only appears after the rate page. */
export function useMarkOptOutRatePageSeen(): void {
  const context = useOptionalOptOutSurvey();
  const markRatePageSeen = context?.markRatePageSeen;

  useEffect(() => {
    markRatePageSeen?.();
  }, [markRatePageSeen]);
}

/** Call from success/completion screens to skip the opt-out survey on close. */
export function useMarkFlowCompleted(): void {
  const context = useOptionalOptOutSurvey();
  const markCompleted = context?.markCompleted;

  useEffect(() => {
    markCompleted?.();
  }, [markCompleted]);
}
